import React, { useState } from "react";
import _ from "lodash";
import { getAllLeadType, getLeadTypeLabel } from "types/leadType";
import { getProductDisplayName } from "types/product";
import { getAllLeadStatus, getLeadStatus, getAssignedStatus } from "types/stateStatus";
import { useGetAllProduct } from "../service/LeadOverviewService";
import { MultiSelectDropdown } from "components/ui/dropdown";
import { SearchInput } from "components/ui/inputs";
import { useNavigate } from "react-router-dom";

interface LeadOverviewFiltersProps {
    onStatusChange: (statuses: number[]) => void;
    onLeadTypeChange: (leadTypes: number[]) => void;
    onProductChange: (products: number[]) => void;
    onNameSearchChange: (searchText: string) => void;
    onAnfragenNumberSearchChange: (searchText: string) => void;
    setPage: React.Dispatch<React.SetStateAction<number>>;
}

const LeadOverviewFilters: React.FC<LeadOverviewFiltersProps> = ({
    onStatusChange,
    onLeadTypeChange,
    onProductChange,
    onNameSearchChange,
    onAnfragenNumberSearchChange,
    setPage
}) => {
    const [selectedStatusItems, setSelectedStatusItems] = useState<number[]>([]);
    const [selectedLeadTypeItems, setSelectedLeadTypeItems] = useState<number[]>([]);
    const [selectedProductItems, setSelectedProductItems] = useState<number[]>([]);
    const [selectedAssigneeItems, setSelectedAssigneeItems] = useState<number[]>([]);
    const [selectedPflegegrad, setSelectedPflegegrad] = useState<number[]>([]);
    const [selectedFinanzierung, setSelectedFinanzierung] = useState<number[]>([]);
    const [selectedLocation, setSelectedLocation] = useState<number[]>([]);

    // New state for view mode: false means list view, true means Kanban view.
    const [isKanban, setIsKanban] = useState(false);

    const { data: productInfo } = useGetAllProduct();
    const navigate = useNavigate();

    const handleStatusChange = (value: any) => {
        const statuses = typeof value === "string" ? value.split(",").map(Number) : value;
        setSelectedStatusItems(statuses);
        onStatusChange(statuses);
        setPage(0);
    };

    const handleAssigneeChange = (value: any) => {
        const assignees = typeof value === "string" ? value.split(",").map(Number) : value;
        setSelectedAssigneeItems(assignees);
        setPage(0);
    }

    const handlePflegegradChange = (value: any) => {
        const pflegegrad = typeof value === "string" ? value.split(",").map(Number) : value;
        setSelectedPflegegrad(pflegegrad);
        setPage(0);
    }

    const handleFinanzierungChange = (value: any) => {
        const finanzierung = typeof value === "string" ? value.split(",").map(Number) : value;
        setSelectedFinanzierung(finanzierung);
        setPage(0);
    }

    const handleLocationChange = (value: any) => {
        const location = typeof value === "string" ? value.split(",").map(Number) : value;
        setSelectedLocation(location);
        setPage(0);
    }


    const handleLeadTypeChange = (value: any) => {
        const leadTypes = typeof value === "string" ? value.split(",").map(Number) : value;
        setSelectedLeadTypeItems(leadTypes);
        onLeadTypeChange(leadTypes);
        setPage(0);
    };

    const handleProductChange = (value: any) => {
        const products = typeof value === "string" ? value.split(",").map(Number) : value;
        setSelectedProductItems(products);
        onProductChange(products);
        setPage(0);
    };

    // Toggle between list and Kanban view
    const toggleViewMode = () => {
        setIsKanban(!isKanban);
    };

    // Redirect to Klient creation page
    const goToKlientCreation = () => {
        navigate("/create-klient");
    };

    const goToTaskOverview = () => {
        navigate("/task-management");
    }

    const getAssignedPflegegrad = (pflegegrad: number) => {
        switch (pflegegrad) {
            case 0:
                return "Pflegegrad 1";
            case 1:
                return "Pflegegrad 2";
            case 2:
                return "Pflegegrad 3";
            case 3:
                return "Pflegegrad 4";
            case 4:
                return "Pflegegrad 5";
            case 5:
                return "Beantragt";
            case 6:
                return "Nicht vorhanden";
            default:
                return `Pflegegrad ${pflegegrad}`;
        }
    };


    const getAssignedFinanzierung = (finanzierung: number) => {
        switch (finanzierung) {
            case 0:
                return "Gesetzliche Krankenversicherung";
            case 1:
                return "Private Krankenversicherung";
            case 2:
                return "Unfallversicherung";
            case 3:
                return "Selbstzahler";
            case 4:
                return "Sozialhilfe";
            case 5:
                return "Pflegeversicherung";
            default:
                return `Finanzierungsart ${finanzierung}`;
        }
    };


    const getAssignedLocation = (location: number) => {
        switch (location) {
            case 0:
                return "Pflege Sorgenlos Mainz";
            case 1:
                return "Pflege Sorgenlos Hamburg";
            case 2:
                return "Pflege Sorgenlos München";
            case 3:
                return "Pflege Sorgenlos Frankfurt";
            case 4:
                return "Pflege Sorgenlos Berlin";
            default:
                return `Standort ${location}`;
        }
    };


    return (
        <div className="toolbar">
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "1rem" }}>
                <button onClick={() => navigate("/leadOverview/won")}
                    className="kanban-switch"
                >
                    <i className={`fa-solid fa-list`} />
                </button>
                <button onClick={() => navigate("/sales-management")}
                    className="kanban-switch"

                >
                    <i className={`fa-solid fa-table-columns`} />
                </button>
                <button onClick={goToTaskOverview}
                    className="kanban-switch"
                >
                    <i className={`fa-solid fa-tasks`} />
                </button>
            </div>
            <SearchInput
                placeholder="Nach ID suchen"
                onSearchChange={onAnfragenNumberSearchChange}
            />
            <SearchInput
                placeholder="Nach Name suchen"
                onSearchChange={onNameSearchChange}
            />
            <MultiSelectDropdown
                options={productInfo || []}
                selectedValues={selectedProductItems}
                onChange={handleProductChange}
                getLabel={getProductDisplayName}
                placeholder="Aufgaben"
            />
            <MultiSelectDropdown
                options={getAllLeadType()}
                selectedValues={selectedLeadTypeItems}
                onChange={handleLeadTypeChange}
                getLabel={getLeadTypeLabel}
                placeholder="Phase"
            />
            <MultiSelectDropdown
                options={getAllLeadStatus()}
                selectedValues={selectedStatusItems}
                onChange={handleStatusChange}
                getLabel={getLeadStatus}
                placeholder="Status"
            />
            <MultiSelectDropdown
                options={[
                    0,
                    1,
                    2,
                    3,
                ]}
                selectedValues={selectedAssigneeItems}
                onChange={handleAssigneeChange}
                getLabel={getAssignedStatus}
                placeholder="Zuständig"
            />
            <MultiSelectDropdown
                options={[
                    0,
                    1,
                    2,
                    3,
                    4,
                    5,
                    6,
                ]}
                selectedValues={selectedPflegegrad}
                onChange={handlePflegegradChange}
                getLabel={getAssignedPflegegrad}
                placeholder="Pflegegrad"
            />

            <MultiSelectDropdown
                options={[
                    0,
                    1,
                    2,
                    3,
                    4,
                    5,

                ]}
                selectedValues={selectedFinanzierung}
                onChange={handleFinanzierungChange}
                getLabel={getAssignedFinanzierung}
                placeholder="Finanzierung"
            />

            <MultiSelectDropdown
                options={[
                    0,
                    1,
                    2,
                    3,
                ]}
                selectedValues={selectedLocation}
                onChange={handleLocationChange}
                getLabel={getAssignedLocation}
                placeholder="Standort"
            />


            <button
                className="template2-button"
                style={{ marginTop: "0px" }}
            >
                <i className="fa-solid fa-plus" /> Neuer Filter
            </button>



        </div>
    );
};

export default LeadOverviewFilters;
